import {useCallback, useEffect, useRef, useState} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {Button} from "./ui/button";
import {Badge} from "./ui/badge";
import {Avatar, AvatarFallback} from "./ui/avatar";
import {Separator} from "./ui/separator";
import {AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
        AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
        AlertDialogTitle, AlertDialogTrigger} from "./ui/alert-dialog";
import {Dialog, DialogContent, DialogDescription, DialogHeader,
        DialogTitle, DialogTrigger} from "./ui/dialog";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "./ui/tooltip";
import {Sparkles, Send, Trash2, Plus, Loader2, Wrench, Paperclip, X, Activity,
        History, Bot, User as UserIcon, MoreHorizontal, ChevronDown, Minimize2,
        Zap, AlertTriangle, ShieldAlert, Gauge, Server, Cable, Bug, Upload,
        ThumbsUp, ThumbsDown, CheckCircle2, ImageIcon} from "lucide-react";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem,
        DropdownMenuSeparator, DropdownMenuTrigger} from "./ui/dropdown-menu";
import {Popover, PopoverContent, PopoverTrigger} from "./ui/popover";
import {cn} from "../lib/utils";

type Role = "user" | "assistant";

interface AttachedImage {
  id: string;
  dataUrl: string;
  mediaType: string;
  base64: string;
}

interface ChatMessage {
  id: string;
  role: Role;
  content: string;
  model?: string;
  images?: string[];
  toolCalls?: Array<{name: string; input: any; done?: boolean}>;
  streaming?: boolean;
  thinkingPhrase?: string;
  serverId?: number;       // id сообщения в БД (для feedback)
  feedback?: "solved" | "unsolved";  // оценка клиента
}

// Прикольные статусы вместо унылого "думаю..."
const THINKING_PHRASES = [
  "думаю…",
  "напрягаю мозги…",
  "копаюсь в логах…",
  "соображаю…",
  "перевариваю…",
  "обмозговываю…",
  "ща, секунду…",
  "так-так-так…",
  "включаю мозг…",
  "анализирую…",
  "ща сообразим…",
  "роюсь в архивах…",
  "ща пойду гляну…",
  "минутку, ща…",
  "стою и думаю…",
  "не торопи, считаю…",
  "тяну носом данные…",
  "перебираю варианты…",
  "ну ка ну ка…",
  "залип на секунду…",
  "погоди, гружу мозги…",
  "ща поглядим…",
  "копаюсь…",
  "сейчас дойдёт…",
  "погружаюсь в тему…",
  "сличаю факты…",
  "перерою всё, найду…",
  "идёт работа, не мешай…",
  "ща-ща-ща…",
  "ваяю ответ…",
];

function pickThinkingPhrase(): string {
  return THINKING_PHRASES[Math.floor(Math.random() * THINKING_PHRASES.length)];
}

async function fileToAttached(file: File): Promise<AttachedImage | null> {
  if (!file.type.startsWith("image/")) return null;
  if (file.size > 5 * 1024 * 1024) {
    alert("Скрин больше 5MB не приму");
    return null;
  }
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(",")[1] || "";
      resolve({
        id: `img-${Date.now()}-${Math.random()}`,
        dataUrl, mediaType: file.type, base64,
      });
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

interface Conversation {
  id: number;
  title: string;
  updated_at: number;
}

interface AssistantWidgetProps {
  currentProject?: {id: string; name: string; running: boolean} | null;
}

const STORAGE_KEY = "ai_assistant_conv_id";

export function AssistantWidget({currentProject}: AssistantWidgetProps) {
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [convId, setConvId] = useState<number | null>(() => {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? parseInt(v, 10) : null;
  });
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [quota, setQuota] = useState<{used: number; limit: number; remaining: number} | null>(null);
  const [attachments, setAttachments] = useState<AttachedImage[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);

  // Idea #104: плавающую кнопку Тимура можно тащить по вертикали (одна ось),
  // чтобы она не перекрывала всплывающие ошибки запуска в правом нижнем углу.
  const [btnDragY, setBtnDragY] = useState<number>(() => {
    const v = Number(localStorage.getItem("ai_widget_btn_y") || "0");
    if (!Number.isFinite(v) || v < 0) return 0;
    // Кламп по текущему окну — сохранённая позиция с большого экрана не должна
    // увести кнопку за верх на маленьком.
    const maxUp = typeof window !== "undefined" ? Math.max(0, window.innerHeight - 120) : v;
    return Math.min(v, maxUp);
  });
  const btnMovedRef = useRef(false);

  // Idea #104: при уменьшении окна после mount вернуть кнопку в видимую зону.
  useEffect(() => {
    const onResize = () => {
      const maxUp = Math.max(0, window.innerHeight - 120);
      setBtnDragY(y => (y > maxUp ? maxUp : y));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [showScrollDown, setShowScrollDown] = useState(false);

  const scrollToBottom = useCallback((smooth = true) => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({top: el.scrollHeight, behavior: smooth ? "smooth" : "auto"});
    });
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    setShowScrollDown(!isAtBottom && el.scrollHeight > el.clientHeight + 100);
  }, []);

  useEffect(() => {
    fetch("/api/assistant/health")
      .then(r => r.json())
      .then(d => setAvailable(!!d.available))
      .catch(() => setAvailable(false));
  }, []);

  const refreshQuota = useCallback(() => {
    fetch("/api/assistant/quota")
      .then(r => r.json())
      .then(d => setQuota({used: d.used || 0, limit: d.limit || 30, remaining: d.remaining || 0}))
      .catch(() => {});
  }, []);

  useEffect(() => { if (open) refreshQuota(); }, [open, refreshQuota]);

  const loadConversations = useCallback(async () => {
    try {
      const r = await fetch("/api/assistant/conversations");
      const d = await r.json();
      setConversations(d.items || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadMessages = useCallback(async (id: number) => {
    try {
      const r = await fetch(`/api/assistant/conversations/${id}/messages`);
      const d = await r.json();
      const msgs: ChatMessage[] = (d.items || []).map((m: any, i: number) => ({
        id: `${id}-${i}`,
        role: m.role,
        content: m.content,
        model: m.model,
        serverId: m.id,
        feedback: m.feedback,
      }));
      setMessages(msgs);
    } catch (e) { console.error(e); }
  }, []);

  const sendFeedback = useCallback(async (msgClientId: string, serverId: number, rating: "solved" | "unsolved") => {
    setMessages(m => m.map(x => x.id === msgClientId ? {...x, feedback: rating} : x));
    try {
      await fetch(`/api/assistant/messages/${serverId}/feedback`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({rating}),
      });
    } catch (e) {
      console.error("feedback send failed", e);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadConversations();
    // Не дёргаем БД в момент стрима — иначе loadMessages() перезатрёт
    // только что отображаемый streaming-ответ Тимура.
    if (convId && !sending) loadMessages(convId);
  }, [open, convId, sending, loadConversations, loadMessages]);

  // Auto-scroll on new messages and on first open of a chat
  useEffect(() => {
    if (messages.length > 0) scrollToBottom(false);
  }, [messages.length, scrollToBottom]);

  // Smoothly follow streaming
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.streaming) scrollToBottom(true);
  }, [messages, scrollToBottom]);

  // Ротация прикольных статусов: меняем фразу каждые 3 сек, пока контент пуст
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last?.streaming || last.content) return;
    const t = setInterval(() => {
      setMessages(m => m.map(msg =>
        msg.id === last.id && msg.streaming && !msg.content
          ? {...msg, thinkingPhrase: pickThinkingPhrase()}
          : msg
      ));
    }, 3000);
    return () => clearInterval(t);
  }, [messages]);

  // When we open the chat — jump to bottom on next tick (after content renders)
  useEffect(() => {
    if (open && !showHistory) {
      setTimeout(() => scrollToBottom(false), 50);
      setTimeout(() => scrollToBottom(false), 200);
    }
  }, [open, showHistory, convId, scrollToBottom]);

  const newConversation = useCallback(() => {
    setConvId(null);
    setMessages([]);
    setAttachments([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const switchConversation = useCallback((id: number) => {
    setConvId(id);
    localStorage.setItem(STORAGE_KEY, String(id));
    loadMessages(id);
    setShowHistory(false);
  }, [loadMessages]);

  const deleteConversation = useCallback(async (id: number) => {
    await fetch(`/api/assistant/conversations/${id}`, {method: "DELETE"});
    if (convId === id) newConversation();
    loadConversations();
  }, [convId, newConversation, loadConversations]);

  const clearCurrentChat = useCallback(async () => {
    if (convId) await fetch(`/api/assistant/conversations/${convId}`, {method: "DELETE"});
    newConversation();
    loadConversations();
  }, [convId, newConversation, loadConversations]);

  const addImageFile = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) return alert("Скрин больше 5MB не приму");
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(",")[1] || "";
      setAttachments(a => [...a, {
        id: `img-${Date.now()}-${Math.random()}`,
        dataUrl, mediaType: file.type, base64,
      }]);
    };
    reader.readAsDataURL(file);
  }, []);

  const onPaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) addImageFile(f);
        e.preventDefault();
      }
    }
  }, [addImageFile]);

  const onPickFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) addImageFile(files[i]);
    e.target.value = "";
  }, [addImageFile]);

  // Drag-and-drop скриншотов прямо в окно чата. На macOS перетаскивание
  // превью-скрина из угла отдаёт файл через dataTransfer.files (а иногда
  // через items.getAsFile). Без preventDefault браузер просто откроет
  // картинку в новой вкладке — это и есть тот баг что ловил пользователь.
  const hasImageDrag = (dt: DataTransfer | null) => {
    if (!dt) return false;
    if (dt.items && dt.items.length) {
      for (let i = 0; i < dt.items.length; i++) {
        if (dt.items[i].kind === "file") return true;
      }
    }
    return Array.from(dt.types || []).includes("Files");
  };

  const onDragEnter = useCallback((e: React.DragEvent) => {
    if (!hasImageDrag(e.dataTransfer)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (!hasImageDrag(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    if (!hasImageDrag(e.dataTransfer)) return;
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    const dt = e.dataTransfer;
    if (!dt) return;
    const seen: File[] = [];
    if (dt.files && dt.files.length) {
      for (let i = 0; i < dt.files.length; i++) seen.push(dt.files[i]);
    } else if (dt.items && dt.items.length) {
      for (let i = 0; i < dt.items.length; i++) {
        const it = dt.items[i];
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) seen.push(f);
        }
      }
    }
    const imgs = seen.filter(f => f.type.startsWith("image/"));
    if (imgs.length === 0) {
      if (seen.length > 0) alert("Перетащи именно картинку/скрин — другие файлы я не приму");
      return;
    }
    imgs.forEach(addImageFile);
  }, [addImageFile]);

  const sendMessage = useCallback(async (text: string, imgs: AttachedImage[]) => {
    if ((!text && imgs.length === 0) || sending) return;
    setSending(true);

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`, role: "user", content: text,
      images: imgs.map(i => i.dataUrl),
    };
    const assistantMsg: ChatMessage = {
      id: `a-${Date.now()}`, role: "assistant", content: "", streaming: true, toolCalls: [],
      thinkingPhrase: pickThinkingPhrase(),
    };
    setMessages(m => [...m, userMsg, assistantMsg]);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const resp = await fetch("/api/assistant/chat", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          message: text,
          conv_id: convId,
          current_project: currentProject ? {
            id: currentProject.id, name: currentProject.name, running: currentProject.running,
          } : null,
          images: imgs.map(i => ({media_type: i.mediaType, data: i.base64})),
        }),
        signal: ctrl.signal,
      });

      if (resp.status === 409 || resp.status === 422) {
        // stale conv_id → сбросим и попросим клиента перепослать (создастся новая)
        localStorage.removeItem(STORAGE_KEY);
        setConvId(undefined);
        throw new Error("__stale_conv__");
      }
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const {done, value} = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, {stream: true});
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const ev of events) {
          if (!ev.startsWith("data:")) continue;
          const payload = ev.slice(5).trim();
          if (!payload) continue;
          let data: any;
          try { data = JSON.parse(payload); } catch { continue; }

          if (data.type === "meta" && data.conv_id) {
            setConvId(data.conv_id);
            localStorage.setItem(STORAGE_KEY, String(data.conv_id));
          }
          if (data.type === "meta" && data.quota_exceeded) {
            setQuota({used: data.used, limit: data.limit, remaining: 0});
          }
          if (data.type === "text") {
            setMessages(m => m.map(msg =>
              msg.id === assistantMsg.id ? {...msg, content: msg.content + data.data} : msg
            ));
          }
          if (data.type === "tool_use") {
            setMessages(m => m.map(msg =>
              msg.id === assistantMsg.id
                ? {...msg, toolCalls: [...(msg.toolCalls || []), {name: data.name, input: data.input}]}
                : msg
            ));
          }
          if (data.type === "tool_result") {
            setMessages(m => m.map(msg => {
              if (msg.id !== assistantMsg.id) return msg;
              const tc = [...(msg.toolCalls || [])];
              for (let i = tc.length - 1; i >= 0; i--) {
                if (tc[i].name === data.name && !tc[i].done) {
                  tc[i] = {...tc[i], done: true};
                  break;
                }
              }
              return {...msg, toolCalls: tc};
            }));
          }
          if (data.type === "done") {
            setMessages(m => m.map(msg =>
              msg.id === assistantMsg.id ? {...msg, streaming: false, model: data.model} : msg
            ));
          }
          if (data.type === "msg_saved" && data.msg_id) {
            setMessages(m => m.map(msg =>
              msg.id === assistantMsg.id ? {...msg, serverId: data.msg_id} : msg
            ));
          }
          if (data.type === "error") {
            setMessages(m => m.map(msg =>
              msg.id === assistantMsg.id
                ? {...msg, streaming: false, content: msg.content + `\n\n*Ошибка: ${data.error}*`}
                : msg
            ));
          }
        }
      }
    } catch (e: any) {
      if (e.name === "AbortError") return;
      // Маскируем все технические ошибки кастомным сообщением (по ТЗ Коли)
      const friendlyMsg = e.message === "__stale_conv__"
        ? "Окно чата освежил, попробуй ещё раз — старая ветка сбросилась."
        : "Другой клиент сейчас выдал очень большой объём данных на решение — все ресурсы ушли на его вопрос. Попробуй секунд через 10.";
      setMessages(m => m.map(msg =>
        msg.id === assistantMsg.id
          ? {...msg, streaming: false, content: msg.content + (msg.content ? "\n\n" : "") + friendlyMsg}
          : msg
      ));
    } finally {
      setSending(false);
      abortRef.current = null;
      loadConversations();
      refreshQuota();
    }
  }, [sending, convId, currentProject, loadConversations, refreshQuota]);

  const send = useCallback(() => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || sending) return;
    const imgs = attachments.slice();
    setInput("");
    setAttachments([]);
    return sendMessage(text, imgs);
  }, [input, attachments, sending, sendMessage]);

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Перетаскивание плавающей кнопки по вертикали (idea #104).
  const onBtnPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // Сброс флага здесь (а не через setTimeout после pointerup) — исключает
    // гонку с отложенным click на touch: чистый тап → moved=false → чат открыт.
    btnMovedRef.current = false;
    const startId = e.pointerId;
    const startY = e.clientY;
    const startOff = btnDragY;
    const maxUp = Math.max(0, window.innerHeight - 120);
    let moved = false;
    let cur = startOff;
    // function-декларации (hoisted) — cleanup видит onMove/onUp/onCancel.
    function cleanup() {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    }
    function onMove(ev: PointerEvent) {
      if (ev.pointerId !== startId) return; // игнор второго пальца (мультитач)
      const delta = startY - ev.clientY;    // тащим вверх → +
      if (Math.abs(delta) > 4) moved = true;
      cur = Math.min(maxUp, Math.max(0, startOff + delta));
      setBtnDragY(cur);
    }
    function onUp(ev: PointerEvent) {
      if (ev.pointerId !== startId) return;
      cleanup();
      try { localStorage.setItem("ai_widget_btn_y", String(Math.round(cur))); } catch {}
      // drag? тогда click (сразу после pointerup) не должен открыть чат
      btnMovedRef.current = moved;
    }
    function onCancel(ev: PointerEvent) {
      if (ev.pointerId !== startId) return;
      cleanup(); // жест отменён системой (шторка/меню) — снять слушатели, click не последует
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  };

  if (available === false) return null;

  return (
    <TooltipProvider delayDuration={300}>
      {!open && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="lg"
              onPointerDown={onBtnPointerDown}
              onClick={() => { if (!btnMovedRef.current) setOpen(true); }}
              style={{ bottom: 24 + btnDragY }}
              title="Перетащи по вертикали, если мешает"
              className="fixed right-6 z-50 h-12 rounded-full px-5 shadow-lg gap-2 touch-none select-none cursor-grab active:cursor-grabbing"
            >
              <Sparkles className="size-4" />
              Тимур AI
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">AI-помощник</TooltipContent>
        </Tooltip>
      )}

      {open && (
        <div
          className={cn(
            "fixed z-50 flex flex-col bg-popover border border-border/80",
            // Усиленная "плавающая" тень — виджет должен заметно отделяться
            "shadow-[0_25px_70px_-10px_rgba(0,0,0,0.7),0_15px_40px_-15px_rgba(0,0,0,0.6)]",
            "ring-1 ring-foreground/10",
            // Mobile: full-screen с малыми отступами; Desktop: floating card в углу
            "inset-x-3 inset-y-3 rounded-xl",
            "sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[460px] sm:h-[min(720px,calc(100dvh-3rem))] sm:rounded-2xl",
            "animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-200",
            "overflow-hidden",
          )}
          role="dialog"
          aria-label="AI-помощник Тимур"
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          {/* Drag-and-drop оверлей: перетащи скрин в окно чата */}
          {dragOver && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3
                            bg-primary/10 backdrop-blur-sm border-2 border-dashed border-primary
                            rounded-xl sm:rounded-2xl pointer-events-none animate-in fade-in-0 duration-100">
              <ImageIcon className="size-10 text-primary" />
              <span className="text-sm font-medium text-primary">Отпусти — загружу скрин</span>
            </div>
          )}

          {/* Header */}
          <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  <Sparkles className="size-4" />
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-0 min-w-0">
                <h2 className="text-sm font-medium leading-tight truncate">Тимур</h2>
                {quota && (
                  <span className={cn(
                    "text-xs font-mono leading-tight",
                    quota.remaining > 5 ? "text-muted-foreground" :
                    quota.remaining > 0 ? "text-amber-600 dark:text-amber-500" :
                    "text-destructive"
                  )}>
                    {quota.used}/{quota.limit}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-0.5 shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-8 w-8"
                    onClick={() => setShowHistory(v => !v)}
                  >
                    <History className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>История</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-8 w-8"
                    onClick={newConversation}
                  >
                    <Plus className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Новый</TooltipContent>
              </Tooltip>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <ClearChatItem
                    hasMessages={messages.length > 0}
                    onConfirm={clearCurrentChat}
                  />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowHistory(true)}>
                    <History className="size-4 mr-2" />
                    Все диалоги
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setOpen(false)}>
                    <Minimize2 className="size-4 mr-2" />
                    Свернуть
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Separator orientation="vertical" className="h-5 mx-1" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-8 w-8"
                    onClick={() => setOpen(false)}
                    aria-label="Закрыть"
                  >
                    <X className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Закрыть</TooltipContent>
              </Tooltip>
            </div>
          </header>

          {showHistory ? (
            <div className="flex-1 overflow-y-auto p-2">
              {conversations.length === 0 && (
                <div className="text-sm text-muted-foreground p-6 text-center">
                  Пока ничего нет
                </div>
              )}
              <div className="space-y-1">
                {conversations.map(c => (
                  <div
                    key={c.id}
                    className={cn(
                      "group flex items-center gap-2 rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors",
                      convId === c.id && "bg-accent text-accent-foreground"
                    )}
                    onClick={() => switchConversation(c.id)}
                  >
                    <span className="flex-1 truncate">{c.title}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                          onClick={(e) => {e.stopPropagation(); deleteConversation(c.id);}}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Удалить</TooltipContent>
                    </Tooltip>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="relative flex-1 min-h-0">
                <div
                  ref={scrollRef}
                  onScroll={onScroll}
                  className="absolute inset-0 overflow-y-auto px-4 py-4 space-y-4"
                >
                  {messages.length === 0 && (
                    <EmptyState onPick={(t) => setInput(t)} />
                  )}
                  {messages.map(msg => (
                    <MessageBubble
                      key={msg.id}
                      msg={msg}
                      onFeedback={sendFeedback}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Floating "scroll down" button */}
                {showScrollDown && (
                  <Button
                    variant="secondary" size="icon"
                    onClick={() => scrollToBottom(true)}
                    className="absolute bottom-3 right-3 h-8 w-8 rounded-full shadow-md"
                    aria-label="Вниз"
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                )}
              </div>

              {/* Input area */}
              <div className="p-3 space-y-2 shrink-0 border-t border-border">
                {currentProject && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-help">
                        <span>контекст:</span>
                        <Badge variant="secondary" className="font-normal gap-1.5">
                          <span className={cn(
                            "size-1.5 rounded-full",
                            currentProject.running ? "bg-emerald-500" : "bg-muted-foreground/50"
                          )} />
                          {currentProject.name}
                        </Badge>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[280px]">
                      Тимур видит что ты сейчас открыл этот проект и будет отвечать
                      про него. Перейдёшь в другой проект — контекст обновится.
                    </TooltipContent>
                  </Tooltip>
                )}

                {messages.length === 0 && attachments.length === 0 && (
                  <div className="flex gap-1.5 flex-wrap">
                    <QuickAction icon={<Activity className="size-3" />}
                      label="Что у меня сейчас"
                      onClick={() => setInput("какой статус, есть проблемы?")} />
                    <QuickAction icon={<AlertTriangle className="size-3" />}
                      label="Топ ошибок"
                      onClick={() => setInput("какие ошибки чаще всего у моих проектов за сутки?")} />
                    <QuickAction icon={<ShieldAlert className="size-3" />}
                      label="Капчи"
                      onClick={() => setInput("сколько капчи ловлю и где её больше всего?")} />
                    <QuickAction icon={<Gauge className="size-3" />}
                      label="Скорость"
                      onClick={() => setInput("какой темп работы за сутки и где провисаю?")} />
                    <QuickAction icon={<Server className="size-3" />}
                      label="Сервер"
                      onClick={() => setInput("как там сервер по нагрузке, всё норм?")} />
                    <QuickAction icon={<Cable className="size-3" />}
                      label="Прокси"
                      onClick={() => setInput("есть проекты которые крутятся без прокси?")} />
                  </div>
                )}

                {attachments.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {attachments.map(a => (
                      <div key={a.id} className="relative group">
                        <img src={a.dataUrl} alt=""
                          className="h-16 w-16 rounded border border-border object-cover" />
                        <button
                          onClick={() => setAttachments(arr => arr.filter(x => x.id !== a.id))}
                          className="absolute -top-1.5 -right-1.5 size-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90"
                        >
                          <X className="size-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Скрытый input для прикрепления */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPickFile}
                  className="hidden"
                />

                {/* Тулбар НАД textarea: [📎] [⚡ Команды] [🐛 Баг-репорт] */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button" variant="outline" size="icon"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                        className="h-9 w-9 shrink-0"
                      >
                        <Paperclip className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Прикрепить файл (или Ctrl+V)</TooltipContent>
                  </Tooltip>

                  {messages.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button" variant="outline" size="sm"
                          disabled={sending}
                          className="h-9 px-3 gap-2 shrink-0"
                        >
                          <Zap className="size-4" />
                          <span>Команды</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent side="top" align="start" className="w-[300px] p-2">
                        <div className="text-xs font-medium text-muted-foreground px-2 pt-1 pb-1.5">
                          Быстрая диагностика
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          <PopoverAction icon={<Activity className="size-3.5" />}
                            label="Что у меня сейчас"
                            onClick={() => setInput("какой статус, есть проблемы?")} />
                          <PopoverAction icon={<AlertTriangle className="size-3.5" />}
                            label="Топ ошибок"
                            onClick={() => setInput("какие ошибки чаще всего у моих проектов за сутки?")} />
                          <PopoverAction icon={<ShieldAlert className="size-3.5" />}
                            label="Капчи"
                            onClick={() => setInput("сколько капчи ловлю и где её больше всего?")} />
                          <PopoverAction icon={<Gauge className="size-3.5" />}
                            label="Скорость"
                            onClick={() => setInput("какой темп работы за сутки и где провисаю?")} />
                          <PopoverAction icon={<Server className="size-3.5" />}
                            label="Сервер"
                            onClick={() => setInput("как там сервер по нагрузке, всё норм?")} />
                          <PopoverAction icon={<Cable className="size-3.5" />}
                            label="Прокси"
                            onClick={() => setInput("есть проекты которые крутятся без прокси?")} />
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}

                  <BugReportDialog
                    disabled={sending}
                    onSubmit={(text, imgs) => sendMessage(text, imgs)}
                  />

                  {messages.length > 0 && (
                    <SubmitChatButton convId={convId} disabled={sending} />
                  )}
                </div>

                {/* Textarea + Send — теперь снизу */}
                <div className="flex gap-2 items-end">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={onKey}
                    onPaste={onPaste}
                    placeholder="Спроси что-нибудь, кинь ссылку или скрин…"
                    rows={3}
                    className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 min-h-[72px] max-h-[160px]"
                    disabled={sending}
                  />
                  <Button
                    onClick={send}
                    disabled={(!input.trim() && attachments.length === 0) || sending}
                    size="icon"
                    className="self-end shrink-0 h-9 w-9"
                  >
                    {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </TooltipProvider>
  );
}

function ClearChatItem({hasMessages, onConfirm}: {hasMessages: boolean; onConfirm: () => void}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem
          onSelect={(e) => e.preventDefault()}
          disabled={!hasMessages}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="size-4 mr-2" />
          Очистить чат
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Очистить текущий чат?</AlertDialogTitle>
          <AlertDialogDescription>
            Все сообщения в этом диалоге удалятся. Другие диалоги в истории останутся.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Отмена</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Очистить
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EmptyState({onPick}: {onPick: (t: string) => void}) {
  return (
    <div className="text-center py-12 space-y-4">
      <Avatar className="h-14 w-14 mx-auto">
        <AvatarFallback className="bg-primary text-primary-foreground">
          <Bot className="size-7" />
        </AvatarFallback>
      </Avatar>
      <div className="space-y-1">
        <p className="text-sm font-medium">Тимур на связи</p>
        <p className="text-xs text-muted-foreground">
          Спрашивай по делу — настройки, проекты, логи.
        </p>
      </div>
      <div className="flex flex-col items-center gap-1.5 text-xs">
        <button onClick={() => onPick("покажи мои проекты")}
          className="text-primary hover:underline">покажи мои проекты</button>
        <button onClick={() => onPick("что такое количество потоков")}
          className="text-primary hover:underline">что такое количество потоков</button>
        <button onClick={() => onPick("проект не запускается, что смотреть")}
          className="text-primary hover:underline">проект не запускается, что смотреть</button>
      </div>
    </div>
  );
}

function QuickAction({label, icon, onClick}: {label: string; icon?: React.ReactNode; onClick: () => void}) {
  return (
    <Button
      variant="outline" size="sm"
      onClick={onClick}
      className="h-7 px-2.5 text-xs gap-1 rounded-full font-normal"
    >
      {icon}
      {label}
    </Button>
  );
}

function PopoverAction({label, icon, onClick}: {label: string; icon?: React.ReactNode; onClick: () => void}) {
  return (
    <Button
      variant="ghost" size="sm"
      onClick={onClick}
      className="justify-start h-8 px-2 text-xs gap-2 font-normal"
    >
      <span className="text-muted-foreground">{icon}</span>
      {label}
    </Button>
  );
}

function MessageBubble({msg, onFeedback}: {
  msg: ChatMessage;
  onFeedback?: (clientId: string, serverId: number, rating: "solved" | "unsolved") => void;
}) {
  const isUser = msg.role === "user";
  const canFeedback = !isUser && !msg.streaming && msg.serverId && msg.content && onFeedback;
  return (
    <div className={cn("flex gap-3", isUser ? "flex-row-reverse" : "flex-row")}>
      <Avatar className="h-7 w-7 shrink-0 mt-0.5">
        <AvatarFallback className={cn(
          "text-xs",
          isUser ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground"
        )}>
          {isUser ? <UserIcon className="size-3.5" /> : <Sparkles className="size-3.5" />}
        </AvatarFallback>
      </Avatar>
      <div className={cn("flex flex-col gap-1 max-w-[80%] min-w-0", isUser ? "items-end" : "items-start")}>
        <div className={cn(
          "rounded-2xl px-3.5 py-2 text-sm break-words shadow-sm min-w-0 max-w-full",
          isUser
            ? "bg-primary text-primary-foreground rounded-tr-sm"
            : "bg-muted text-foreground rounded-tl-sm border border-border/40"
        )}>
          {isUser ? (
            <div className="space-y-2">
              {msg.images && msg.images.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {msg.images.map((src, i) => (
                    <img key={i} src={src} alt=""
                      className="max-h-32 rounded border border-primary-foreground/20 object-cover" />
                  ))}
                </div>
              )}
              {msg.content && <p className="whitespace-pre-wrap">{msg.content}</p>}
            </div>
          ) : (
            <div className={cn(
              "text-sm leading-relaxed",
              "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
              "[&_p]:my-1.5",
              "[&_strong]:font-semibold",
              "[&_em]:italic",
              "[&_pre]:my-2 [&_pre]:max-w-full [&_pre]:bg-background [&_pre]:border [&_pre]:border-border [&_pre]:rounded-md [&_pre]:p-2 [&_pre]:overflow-x-auto",
              "[&_pre_code]:text-xs [&_pre_code]:font-mono [&_pre_code]:p-0 [&_pre_code]:bg-transparent [&_pre_code]:border-0",
              "[&_:not(pre)>code]:text-xs [&_:not(pre)>code]:font-mono [&_:not(pre)>code]:bg-background [&_:not(pre)>code]:border [&_:not(pre)>code]:border-border [&_:not(pre)>code]:rounded [&_:not(pre)>code]:px-1 [&_:not(pre)>code]:py-0.5 [&_:not(pre)>code]:[overflow-wrap:anywhere] [&_:not(pre)>code]:[word-break:break-word]",
              "[&_ul]:my-1.5 [&_ul]:pl-5 [&_ul]:list-disc",
              "[&_ol]:my-1.5 [&_ol]:pl-5 [&_ol]:list-decimal",
              "[&_li]:my-0.5",
              "[&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-1.5",
              "[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-2.5 [&_h2]:mb-1",
              "[&_h3]:text-sm [&_h3]:font-medium [&_h3]:mt-2 [&_h3]:mb-1",
              "[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:no-underline",
              "[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:my-2 [&_blockquote]:text-muted-foreground",
              "[&_table]:my-2 [&_table]:text-xs [&_table]:w-full [&_table]:border-collapse",
              "[&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:bg-background",
              "[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1",
              "[&_hr]:my-3 [&_hr]:border-border",
            )}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {msg.content || (msg.streaming ? `_${msg.thinkingPhrase || "думаю…"}_` : "")}
              </ReactMarkdown>
            </div>
          )}

          {msg.toolCalls && msg.toolCalls.length > 0 && (() => {
            const labels: Record<string, string> = {
              // Read-only — диагностика и просмотр
              list_projects: "смотрит проекты",
              get_project_status: "проверяет статус проекта",
              get_campaign_settings: "смотрит настройки проекта",
              get_recent_logs: "читает логи",
              get_panel_info: "проверяет панель",
              panel_overview: "проверяет всё",
              diagnostics: "запускает диагностику",
              fetch_panel_url: "смотрит страницу",
              lookup_setting: "ищет в базе знаний",
              lookup_setting_kb: "ищет в базе знаний",
              search_resolved_bugs: "ищет похожие баги",
              create_bug_ticket: "создаёт тикет",
              // Backend уже подменяет имя на алиас (через _TOOL_PUBLIC_ALIAS):
              "анализирует ситуацию": "анализирует ситуацию",
              "оформляет тикет": "оформляет тикет",
              "запускает проект": "запускает проект",
              "останавливает проект": "останавливает проект",
              "меняет настройки проекта": "меняет настройки проекта",
              "создаёт проект": "создаёт проект",
              "копирует проект": "копирует проект",
              "удаляет проект": "удаляет проект",
              "загружает файл в проект": "загружает файл в проект",
              "читает файл проекта": "читает файл проекта",
              "правит файл проекта": "правит файл проекта",
              read_campaign_file: "читает файл проекта",
              edit_campaign_file: "правит файл проекта",
              "смотрит настройки проекта": "смотрит настройки проекта",
            };
            const active = msg.toolCalls.filter(t => !t.done);
            const done = msg.toolCalls.filter(t => t.done);
            return (
              <>
                {/* Активные действия — заметным индикатором ВВЕРХУ */}
                {active.length > 0 && msg.streaming && (
                  <div className="mb-2 flex items-center gap-2 px-2 py-1.5 rounded-md bg-primary/10 border border-primary/20">
                    <Loader2 className="size-3.5 animate-spin shrink-0 text-primary" />
                    <span className="text-xs font-medium text-primary">
                      Тимур: {labels[active[0].name] || active[0].name}…
                    </span>
                  </div>
                )}
                {/* Завершённые действия — мелким сводным списком */}
                {done.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-current/10 space-y-0.5">
                    {done.map((tc, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[11px] opacity-50">
                        <Wrench className="size-2.5" />
                        <span className="truncate">{labels[tc.name] || tc.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isUser && msg.model && !msg.streaming && (
            <Badge variant="outline" className="text-[10px] font-normal h-4 px-1.5">
              {msg.model.includes("opus") ? "Тимур Николаевич" : "Тимур"}
            </Badge>
          )}

          {/* Feedback кнопки — только под ответом Тимура, после стриминга */}
          {canFeedback && !msg.feedback && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className="opacity-70">помогло?</span>
              <button
                onClick={() => onFeedback!(msg.id, msg.serverId!, "solved")}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-emerald-500/10 hover:text-emerald-600 transition-colors"
                title="Вопрос решён — закрепить ответ Тимура для обучения"
              >
                <ThumbsUp className="size-3" />
                да
              </button>
              <button
                onClick={() => onFeedback!(msg.id, msg.serverId!, "unsolved")}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-rose-500/10 hover:text-rose-600 transition-colors"
                title="Вопрос не решён — отправить тикет команде на разбор"
              >
                <ThumbsDown className="size-3" />
                нет
              </button>
            </div>
          )}
          {canFeedback && msg.feedback === "solved" && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 bg-emerald-500/10 text-emerald-600 border-emerald-500/30 gap-1">
              <CheckCircle2 className="size-2.5" />
              отмечено как решённое
            </Badge>
          )}
          {canFeedback && msg.feedback === "unsolved" && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 bg-rose-500/10 text-rose-600 border-rose-500/30 gap-1">
              <ThumbsDown className="size-2.5" />
              отправлено на разбор
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function SubmitChatButton({convId, disabled}: {
  convId: number | null;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!convId) return;
    setSending(true);
    try {
      const r = await fetch("/api/assistant/submit-chat", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({conversation_id: convId, note}),
      });
      const d = await r.json().catch(() => ({}));
      if (d.ok) {
        setDone(true);
        setTimeout(() => {setOpen(false); setDone(false); setNote("");}, 1800);
      } else {
        alert("Не удалось отправить: " + (d.error || "ошибка"));
      }
    } catch {
      alert("Не удалось отправить — попробуй позже");
    } finally {
      setSending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => {if (!v) {setNote(""); setDone(false);} setOpen(v);}}>
      <Tooltip>
        <TooltipTrigger asChild>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="outline" size="sm" disabled={disabled}
                    className="gap-2 h-9 px-3 shrink-0">
              <Upload className="size-4 text-primary" />
              <span>На анализ</span>
            </Button>
          </AlertDialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Отправить этот чат нашей команде для разбора</TooltipContent>
      </Tooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Отправить чат на анализ?</AlertDialogTitle>
          <AlertDialogDescription className="leading-relaxed">
            Весь текущий диалог (твои сообщения и ответы Тимура) уйдёт нашей команде —
            разберём, если ассистент отвечает не так, и улучшим его. Спасибо, что помогаешь!
          </AlertDialogDescription>
        </AlertDialogHeader>
        {done ? (
          <div className="flex items-center gap-2 text-emerald-500 text-sm py-3">
            <CheckCircle2 className="size-5" /> Отправлено — спасибо за фидбэк!
          </div>
        ) : (
          <>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Что именно не так с ответами? (необязательно)"
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={sending}>Отмена</AlertDialogCancel>
              <Button onClick={submit} disabled={sending || !convId} className="gap-1.5">
                {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                Отправить
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}


function BugReportDialog({onSubmit, disabled}: {
  onSubmit: (text: string, images: AttachedImage[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");
  const [projectUrl, setProjectUrl] = useState("");
  const [shots, setShots] = useState<AttachedImage[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setTitle(""); setDescription(""); setSteps("");
    setExpected(""); setActual(""); setProjectUrl(""); setShots([]);
  };

  const onPickShots = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = 5 - shots.length;
    const toLoad = Array.from(files).slice(0, remaining);
    const loaded: AttachedImage[] = [];
    for (const f of toLoad) {
      const att = await fileToAttached(f);
      if (att) loaded.push(att);
    }
    setShots(s => [...s, ...loaded]);
    e.target.value = "";
  };

  const submit = () => {
    if (!title.trim()) {
      alert("Заголовок обязателен");
      return;
    }
    const lines = ["**Сообщение о баге**", "", `**Заголовок:** ${title.trim()}`];
    if (description.trim()) {
      lines.push("", "**Описание:**", description.trim());
    }
    if (steps.trim()) {
      lines.push("", "**Шаги воспроизведения:**", steps.trim());
    }
    if (expected.trim()) {
      lines.push("", "**Ожидаемое поведение:**", expected.trim());
    }
    if (actual.trim()) {
      lines.push("", "**Фактическое поведение:**", actual.trim());
    }
    if (projectUrl.trim()) {
      lines.push("", `**Ссылка на проект:** ${projectUrl.trim()}`);
    }
    if (shots.length) {
      lines.push("", `_Прикреплено скриншотов: ${shots.length}_`);
    }
    lines.push("", "Помоги разобраться.");
    onSubmit(lines.join("\n"), shots);
    reset();
    setOpen(false);
  };

  const filledCount = [title, description, steps, expected, actual].filter(s => s.trim()).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
      <DialogTrigger asChild>
        <Button
          type="button" variant="outline" size="sm"
          disabled={disabled}
          className="gap-2 h-9 px-3"
        >
          <Bug className="size-4 text-destructive" />
          <span>Баг-репорт</span>
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-[560px] max-h-[92dvh] overflow-y-auto p-0 gap-0">
        {/* Кастомный header с акцентом */}
        <div className="relative bg-gradient-to-br from-destructive/5 via-card to-card border-b border-border px-6 pt-5 pb-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 size-10 rounded-lg bg-destructive/10 flex items-center justify-center">
              <Bug className="size-5 text-destructive" />
            </div>
            <div className="flex-1 min-w-0 space-y-1">
              <DialogTitle className="text-base leading-tight">
                Новый баг-репорт
              </DialogTitle>
              <DialogDescription className="text-xs leading-relaxed">
                Заполни поля — Тимур получит структурированную инфу и сразу разберётся,
                без уточняющих вопросов.
              </DialogDescription>
            </div>
            {filledCount > 0 && (
              <Badge variant="secondary" className="font-mono text-[10px] shrink-0">
                {filledCount}/5
              </Badge>
            )}
          </div>
        </div>

        {/* Тело формы */}
        <div className="px-6 py-5 space-y-4">
          <BugField
            label="Заголовок" required
            hint="Одно предложение — суть проблемы"
          >
            <input
              value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Например: «После рестарта проекта пропадают воркеры»"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            />
          </BugField>

          <BugField
            label="Описание"
            hint="Контекст: когда началось, что менялось, какая версия"
          >
            <textarea
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Например: «началось после обновления, до этого работало неделю»"
              rows={3}
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            />
          </BugField>

          <BugField
            label="Шаги воспроизведения"
            hint="По пунктам, чтобы можно было повторить"
          >
            <textarea
              value={steps} onChange={e => setSteps(e.target.value)}
              placeholder={"1. Открыл проект b8d6...\n2. Нажал «Запуск»\n3. Через 5 минут все воркеры исчезли"}
              rows={4}
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 font-mono leading-relaxed"
            />
          </BugField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <BugField label="Ожидаемое" hint="Что должно было быть">
              <textarea
                value={expected} onChange={e => setExpected(e.target.value)}
                placeholder="Воркеры должны работать"
                rows={3}
                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              />
            </BugField>
            <BugField label="Фактическое" hint="Что получил по факту">
              <textarea
                value={actual} onChange={e => setActual(e.target.value)}
                placeholder="Все умерли через 5 минут"
                rows={3}
                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              />
            </BugField>
          </div>

          <Separator />

          <BugField
            label="Ссылка на проект"
            optional
            hint="Поможет Тимуру быстрее открыть твои настройки"
          >
            <input
              value={projectUrl} onChange={e => setProjectUrl(e.target.value)}
              placeholder="https://твоя-панель/projects/abc123…"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 font-mono text-xs"
            />
          </BugField>

          <BugField
            label="Скриншоты"
            optional
            hint={`До 5 штук. Сейчас: ${shots.length}/5`}
          >
            <input
              ref={fileRef} type="file" accept="image/*" multiple
              onChange={onPickShots} className="hidden"
            />
            <div className="flex flex-wrap gap-2 items-start">
              {shots.map(s => (
                <div key={s.id} className="relative group">
                  <img src={s.dataUrl} alt=""
                    className="h-16 w-16 rounded-md border border-border object-cover" />
                  <button
                    type="button"
                    onClick={() => setShots(arr => arr.filter(x => x.id !== s.id))}
                    className="absolute -top-1.5 -right-1.5 size-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 shadow-sm"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
              {shots.length < 5 && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="h-16 w-16 rounded-md border border-dashed border-border hover:border-ring hover:bg-accent/30 flex flex-col gap-0.5 items-center justify-center text-muted-foreground transition-colors"
                >
                  <Upload className="size-4" />
                  <span className="text-[10px] font-mono">{shots.length}/5</span>
                </button>
              )}
            </div>
          </BugField>
        </div>

        {/* Footer с акцентом */}
        <div className="border-t border-border bg-muted/30 px-6 py-3 flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {title.trim() ? "Готово к отправке" : "Заполни хотя бы заголовок"}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => { reset(); setOpen(false); }}>
              Отмена
            </Button>
            <Button onClick={submit} disabled={!title.trim()} size="sm" className="gap-1.5">
              <Send className="size-3.5" />
              Отправить Тимуру
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BugField({
  label, required, optional, hint, children,
}: {
  label: string; required?: boolean; optional?: boolean; hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label className="text-xs font-medium text-foreground">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
          {optional && <span className="text-muted-foreground ml-1.5 font-normal">(необяз.)</span>}
        </label>
        {hint && <span className="text-[11px] text-muted-foreground truncate">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
